// @ts-ignore
import AOS from "aos";
import type { DirectiveBinding, ObjectDirective } from "vue";

type AosBinding =
  | string
  | {
    name?: string;
    once?: boolean;
    delay?: number;
    duration?: number;
    offset?: number;
    anchorPlacement?: string;
  };

const applyAosAttributes = (el: HTMLElement, binding: DirectiveBinding<AosBinding>) => {
  const value = binding.value;

  if (!value) {
    delete el.dataset.aos;
    delete el.dataset.aosOnce;
    delete el.dataset.aosDelay;
    delete el.dataset.aosDuration;
    delete el.dataset.aosOffset;
    delete el.dataset.aosAnchorPlacement;
    return;
  }

  const options = typeof value === "string" ? { name: value } : value;
  if (!options.name) return;

  el.dataset.aos = options.name;

  if (options.once != null) el.dataset.aosOnce = String(options.once);
  else delete el.dataset.aosOnce;

  if (options.delay != null) el.dataset.aosDelay = String(options.delay);
  else delete el.dataset.aosDelay;

  if (options.duration != null) el.dataset.aosDuration = String(options.duration);
  else delete el.dataset.aosDuration;

  if (options.offset != null) el.dataset.aosOffset = String(options.offset);
  else delete el.dataset.aosOffset;

  if (options.anchorPlacement)
    el.dataset.aosAnchorPlacement = String(options.anchorPlacement);
  else delete el.dataset.aosAnchorPlacement;
};

const createAosDirective = (): ObjectDirective<HTMLElement, AosBinding> => ({
  mounted(el, binding) {
    applyAosAttributes(el, binding);
    requestAnimationFrame(() => AOS.refreshHard());
  },
  updated(el, binding) {
    applyAosAttributes(el, binding);
    requestAnimationFrame(() => AOS.refreshHard());
  },
});

export default defineNuxtPlugin((nuxtApp) => {
  if (process.server) return;

  AOS.init({
    once: true,
    duration: 700, // slow down the animations a bit
    easing: "ease-out-cubic",
  });

  const directive = createAosDirective();
  nuxtApp.vueApp.directive("aos", directive);
  nuxtApp.vueApp.directive("animateonscroll", directive);

  nuxtApp.hook("page:finish", () => {
    requestAnimationFrame(() => AOS.refreshHard());
  });

  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      requestAnimationFrame(() => AOS.refreshHard());
    }, 250);
  });
});
